import React, { useState } from 'react';

import { BookingsSearchForm } from '@components/admin/booking/BookingsSearchForm';
import { BookingsTable } from '@components/admin/booking/BookingsTable';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { ExportCSVDialog2 } from '@components/ExportCSVDialog2';
import { adminBookingInquiryTradeCondtionFilters } from '@data/adminBookingInquiry';
import { bookingsStrings, bookingsStringsCN } from '@data/bookings';
import { useSearch } from '@hooks/next/useSearch';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import { useUser } from '@hooks/queries/useUser';
import { DashboardPreAlertSearchType, type PreAlertType } from '@models/next/PreAlert';
import { makeMuiStyles, useCommonStyles } from '@styles';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
}));

export const CanceledBookings: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search } = useSearch(DashboardPreAlertSearchType);

  const { checklist = [], ...preAlertSearch } = search;

  const query = {
    ...preAlertSearch,
    ...(search.trade_condition &&
      adminBookingInquiryTradeCondtionFilters.get(search.trade_condition)),
    page: search.page ?? 1,
    page_size: search.page_size ?? 50,
    type: ['past_canceled'] as PreAlertType[],
  };

  const user = useUser();

  const routePlans = useRoutePlanList({});

  const podOptions = uniq(routePlans.data?.map((routePlan) => routePlan.pod)).map((pod) => ({
    value: pod,
    label: pod,
  }));
  const polOptions = uniq(routePlans.data?.map((routePlan) => routePlan.pol)).map((pol) => ({
    value: pol,
    label: pol,
  }));

  const isNotFCChina = user.data?.role && user.data?.role !== 'FCChina';

  const title = isNotFCChina
    ? bookingsStrings.title()['past_canceled']
    : `${bookingsStrings.title()['past_canceled']}(${bookingsStringsCN.title()['past_canceled']})`;

  const [UIState, setUIState] = useState(() => ({
    exportDialog: {
      open: false,
    },
  }));

  return (
    <Layout title={title} loading={user.isLoading} errors={[user.error]}>
      <ExportCSVDialog2
        open={UIState.exportDialog.open}
        onClose={() => {
          setUIState((state) => {
            return { ...state, exportDialog: { open: false } };
          });
        }}
        subject={bookingsStrings.booking}
        title={`${title} ${dayjs().format('YYYY-MM-DD HH:mm')}.csv`}
        baseUrl={'/dashboard/pre-alert/'}
        query={query}
        selected={checklist}
      />

      <div className={classes.container}>
        <SectionTitle
          actions={
            <Button
              onClick={() => {
                setUIState((state) => {
                  return { ...state, exportDialog: { open: true } };
                });
              }}
              color="primary"
              text={bookingsStrings.exportCSV}
              shape="round"
            />
          }
        >
          {title}
        </SectionTitle>

        <BookingsSearchForm
          tagFields={[
            'd2d_no',
            'consignee',
            'pol',
            'pod',
            'member_salesperson',
            'salesperson_japan',
          ]}
          rangeFields={['eta', 'etd', 'delivery_instruction_preferred_delivery_date']}
          fieldsInputOptions={{
            pol: { type: 'choose', options: polOptions },
            pod: { type: 'choose', options: podOptions },
          }}
        />

        <BookingsTable
          queryParams={query}
          columns={[
            'shipper',
            'consignee',
            'status',
            'cancel_detail',
            'member_salesperson_last_name',
            'salesperson_japan_last_name',
            'salesperson_china_last_name',
            'pol',
            'pod',
          ]}
          user={user.data}
          podOptions={podOptions}
          polOptions={polOptions}
          type="past_canceled"
        />
      </div>
    </Layout>
  );
};
